import React, { useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { BookOpenCheck, Braces, Download, FileCheck2, FolderKanban, Sparkles } from "lucide-react";
import { db } from "../Lib/Firebase";
import { createFreeResourcePdf } from "../data/freeResourcePdf";
import { defaultFreeResources, FreeResource, mergeFreeResources } from "../data/freeResources";

const visuals = [
    { icon: <FileCheck2 className="h-6 w-6" />, accent: "from-blue-600 to-cyan-500" },
    { icon: <BookOpenCheck className="h-6 w-6" />, accent: "from-indigo-600 to-blue-500" },
    { icon: <Braces className="h-6 w-6" />, accent: "from-cyan-500 to-teal-400" },
    { icon: <FolderKanban className="h-6 w-6" />, accent: "from-orange-500 to-amber-400" },
];

function ResourceDownloadLink({ resource }: { resource: FreeResource }) {
    const [url, setUrl] = useState("");
    useEffect(() => {
        const nextUrl = URL.createObjectURL(createFreeResourcePdf(resource));
        setUrl(nextUrl);
        return () => URL.revokeObjectURL(nextUrl);
    }, [resource]);
    const fileName = resource.fileName.endsWith(".pdf") ? resource.fileName : `${resource.fileName}.pdf`;
    return <a href={url || undefined} download={fileName} aria-label={`Download ${resource.title}`} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#075dcc] to-[#38aee8] px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-blue-500/20 transition hover:brightness-105"><Download className="h-4 w-4" />Download PDF</a>;
}

export default function StudentFreeResources() {
    const [resources, setResources] = useState<FreeResource[]>(defaultFreeResources);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => onSnapshot(collection(db, "freeResources"), (snapshot) => {
        setResources(mergeFreeResources(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))));
        setLoading(false);
        setError("");
    }, () => {
        setLoading(false);
        setError("Live resource updates are temporarily unavailable. Showing the MockZo starter library.");
        setResources(defaultFreeResources);
    }), []);

    return <div className="space-y-5">
        <section className="overflow-hidden rounded-3xl bg-gradient-to-r from-[#061a4a] via-[#075dcc] to-[#38aee8] p-6 text-white shadow-xl sm:p-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-black uppercase tracking-wider"><Sparkles className="h-4 w-4" />Unlocked for students</div>
            <h1 className="mt-4 text-2xl font-black sm:text-3xl">Free career resource library</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-blue-50">Read the full guides and download all four MockZo PDFs from your student portal.</p>
        </section>
        {loading && <p role="status" className="rounded-2xl bg-white p-4 text-slate-500">Loading your resources…</p>}
        {error && <p role="alert" className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{error}</p>}
        <section className="grid gap-5 md:grid-cols-2">
            {resources.map((resource, index) => {
                const visual = visuals[index] || visuals[0];
                return <article key={resource.id} className="flex flex-col rounded-3xl border border-blue-100 bg-white p-6 shadow-xl shadow-blue-900/5 sm:p-7">
                    <div className="flex items-start justify-between gap-4"><span className={`grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br ${visual.accent} text-white shadow-lg`}>{visual.icon}</span><span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-black uppercase tracking-wide text-emerald-700">Student access</span></div>
                    <h2 className="mt-5 text-xl font-extrabold text-slate-900">{resource.title}</h2>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{resource.description}</p>
                    <ul className="mt-4 space-y-2 text-sm text-slate-700">{resource.points.map((point) => <li key={point} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-blue-500" />{point}</li>)}</ul>
                    <div className="mt-auto flex items-center justify-between gap-3 pt-6"><span className="text-xs font-bold uppercase tracking-wide text-slate-400">{resource.format}</span><ResourceDownloadLink resource={resource} /></div>
                </article>;
            })}
        </section>
    </div>;
}
